import React, { useState, useMemo } from 'react';
import { useParams } from '@tanstack/react-router';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/NewButton";
import { Move, Folder as FolderIcon } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useFolderTree, moveFolder, Folder, FolderTree } from '@/hooks/useFolders';
import { apiErrorMessage } from '@/lib/api-client';
import { flattenFolderTree } from '@/utils/document-utils';

interface MoveFolderDialogProps {
  isOpen: boolean;
  onClose: () => void;
  folder?: Folder;
  onSuccess: () => void;
}

export const MoveFolderDialog: React.FC<MoveFolderDialogProps> = ({
  isOpen,
  onClose,
  folder,
  onSuccess
}) => {
  const { slug: matterSlug } = useParams({ from: '/matter/$slug' });
  const [selectedParentId, setSelectedParentId] = useState<string>('');
  const [isMoving, setIsMoving] = useState(false);

  // Use the custom hook for folder tree
  const { folderTree, isLoading } = useFolderTree(matterSlug);

  // Check if destination has a folder with the same name
  const hasNameConflict = () => {
    if (!folder || !selectedParentId) return false;

    // Get siblings at the destination
    let destinationChildren: FolderTree[] = [];

    if (selectedParentId === 'root') {
      // Check root-level folders
      destinationChildren = folderTree || [];
    } else {
      // Find the destination folder and get its children
      const findFolder = (folders: FolderTree[]): FolderTree | null => {
        for (const f of folders) {
          if (f.folder_id.toString() === selectedParentId) {
            return f;
          }
          if (f.children && f.children.length > 0) {
            const found = findFolder(f.children);
            if (found) return found;
          }
        }
        return null;
      };

      const destinationFolder = folderTree ? findFolder(folderTree) : null;
      destinationChildren = destinationFolder?.children || [];
    }

    // Check if any sibling has the same name
    return destinationChildren.some(child =>
      child.name.toLowerCase() === folder.name.toLowerCase() &&
      child.folder_id !== folder.folder_id
    );
  };

  const handleMoveFolder = async () => {
    if (!matterSlug || !folder) return;

    if (hasNameConflict()) {
      toast.error('A folder with this name already exists in the destination');
      return;
    }

    try {
      setIsMoving(true);

      const moveData = {
        parent_folder_id: selectedParentId === 'root' ? undefined : parseInt(selectedParentId)
      };

      await moveFolder(matterSlug, folder.guid, moveData);

      toast.success('Folder moved successfully');
      onSuccess();
      onClose();
      setSelectedParentId('');
    } catch (error: any) {
      console.error('Error moving folder:', error);
      toast.error(apiErrorMessage(error) || 'Failed to move folder');
    } finally {
      setIsMoving(false);
    }
  };

  const handleClose = () => {
    setSelectedParentId('');
    onClose();
  };

  // Flatten folder tree for selection, excluding current folder and descendants
  const flatFolders = useMemo(() => {
    if (!folderTree) return [];

    // Filter function to exclude current folder and its descendants
    const excludeCurrentFolder = (folderOption: FolderTree) => {
      if (!folder) return true;
      // Skip the current folder and its descendants
      return folderOption.folder_id !== folder.folder_id &&
        !folderOption.path.startsWith(folder.path);
    };

    return flattenFolderTree(folderTree, 0, excludeCurrentFolder);
  }, [folderTree, folder]);

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Move className="h-5 w-5" />
            Move Folder
          </DialogTitle>
          <DialogDescription>
            Move "{folder?.name}" to a different location.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Move to:</label>
            <Select value={selectedParentId} onValueChange={setSelectedParentId}>
              <SelectTrigger>
                <SelectValue placeholder="Select destination folder" />
              </SelectTrigger>
              <SelectContent position="popper">
                <SelectGroup>
                  <SelectItem
                    value="root"
                    className="group cursor-pointer hover:bg-accent/50 transition-colors duration-150"
                  >
                    <div className="flex items-center">
                      <FolderIcon className="h-4 w-4 mr-2 shrink-0 text-amber-500/40 fill-amber-400/15" />
                      <span className="font-medium">Root (Top Level)</span>
                    </div>
                  </SelectItem>
                  {flatFolders.map((folderOption) => {
                    const folderId = folderOption.folder_id;
                    if (!folderId) return null;
                    return (
                      <SelectItem
                        key={`move-folder-${folderId}`}
                        value={folderId.toString()}
                        className="group cursor-pointer hover:bg-accent/50 transition-colors duration-150"
                        style={{ paddingLeft: `${folderOption.depth * 16 + 32}px` }}
                      >
                        <div className="flex items-center w-full">
                          <FolderIcon className="h-4 w-4 mr-2 shrink-0 text-amber-500/40 fill-amber-400/15" />
                          <span className="truncate font-medium">{folderOption.displayName}</span>
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectGroup>
              </SelectContent>
            </Select>
            {hasNameConflict() && (
              <p className="text-sm text-red-500">
                A folder named "{folder?.name}" already exists in this location
              </p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isMoving} text='Cancel' />
          <Button
            onClick={handleMoveFolder}
            disabled={isMoving || !selectedParentId || isLoading || hasNameConflict()}
            loading={isLoading}
            text='Move'
          />
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
